import { LineCap, LineJoin } from '@tencent/alloy-renderer';
import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import {
    IParagraph,
    ITextRun,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import {
    clear,
    initTextRun,
} from '@editor/src/page/index/components/canvas/element-types/shape-element/utils';
import { IGraphicsOptions, StrokeType, GraphicsType } from '@tencent/alloy-graphics';
import { EVENT_EXPAND_STROKE_WIDTH } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-graphic/constants';

const NORMAL_LINE_WIDTH = 1;
const TEMPLATE_LINE_WIDTH = 4;

export function generatePlaceHolderModel(frame, isTemplate = false, scale = 1): IGraphicsOptions[] {
    const { width, height } = frame;
    const brightBackgroundColor = isTemplate ? '#c2c2c2' : 'rgba(0, 0, 0, .16)';
    const darkBackgroundColor = isTemplate ? '#ffffff' : 'rgba(255, 255, 255, .32)';
    const lineWidth = isTemplate ? TEMPLATE_LINE_WIDTH : NORMAL_LINE_WIDTH;
    const offset = lineWidth / 2;

    const rectInfo = [{
        frame: {
            left: ptToCanvas(offset),
            top: ptToCanvas(offset),
            width: width - ptToCanvas(lineWidth),
            height: height - ptToCanvas(lineWidth),
        },
        color: brightBackgroundColor,
    }, {
        frame: {
            left: ptToCanvas(-offset),
            top: ptToCanvas(-offset),
            width: width + ptToCanvas(lineWidth),
            height: height + ptToCanvas(lineWidth),
        },
        color: darkBackgroundColor,
    }];

    return rectInfo.map(rect => generateGraphicModel(rect.frame, rect.color, scale, lineWidth));
}

function generateGraphicModel(frame, color: string, scale, lineWidth: number): IGraphicsOptions {
    const { width, height, left, top } = frame;

    return {
        frame: {
            left,
            top,
            width,
            height,
        },
        path: {
            type: GraphicsType.RECT,
        },
        strokeStyle: {
            lineWidth: ptToCanvas(lineWidth),
            fillStyle: {
                solidFill: {
                    color,
                },
            },
            lineCap: LineCap.Butt,
            lineJoin: LineJoin.Miter,
            type: StrokeType.SOLID,
            hitStrokeWidth: ptToCanvas(lineWidth) + (EVENT_EXPAND_STROKE_WIDTH / scale),
        },
        fillStyle: {
            noFill: true,
        },
    };
}

/**
 * 修改place holder段落信息，生成UI显示的段落数据。无需拷贝直接修改同步到model
 * @param component
 */
export function initPlaceHolderParagraphList(component): IParagraph[] {
    const myPHL = initTextRun(component);
    clear(component.paragraphList, false); // placeholder清除文本内容（保存时也不需要）
    return mergeEndStyleToRun(myPHL);
}

/**
 * 修改place holder保存的段落数据
 * @param paragraphList
 */
export function updateSavedPlaceHolderParagraphList(paragraphList: IParagraph[]): IParagraph[] {
    mergeRunToEndStyle(paragraphList);
    return clear(paragraphList, false);
}

/**
 * 合并run中的属性到endStyle
 * @param paragraphList
 */
function mergeRunToEndStyle(paragraphList: IParagraph[]) {
    paragraphList?.forEach((para: IParagraph) => {
        const firstRunStyle = para.textRuns?.[0]?.styles;
        /**
         * 几个不同的填充使用了不同的字段，当从一种填充变为另一种填充时无法被自动merge
         * run中的填充数据是准确的，当没有这个key的时候手动加一个undefined以进行merge
         */
        const fillKeys = ['color', 'gradFill', 'image', 'pattFill', 'noFill'];
        fillKeys.forEach((key) => {
            if (firstRunStyle && !firstRunStyle[key]) {
                firstRunStyle[key] = undefined;
            }
        });
        para.endParaRPr = { ...para.endParaRPr, ...firstRunStyle };
    });
    return paragraphList;
}

/**
 * 合并段落的endParaRPr到run的style中
 * @param paragraphList
 */
function mergeEndStyleToRun(paragraphList: IParagraph[]) {
    paragraphList?.forEach((para: IParagraph) => {
        const endRunStyle = para.endParaRPr;
        para.textRuns?.forEach((run: ITextRun) => {
            // merge endParaRPr
            run.styles = { ...run.styles, ...endRunStyle };
        });
    });
    return paragraphList;
}

/**
 * paceHolder保存endParaRPr
 * @param model
 */
export function savePlaceHolderParaEndProps(model) {
    const { paragraphList } = model;
    if (paragraphList[0]?.endParaRPr) {
        model.savedEndParaRPr = { ...paragraphList[0]?.endParaRPr };
        delete model.savedEndParaRPr.useEndParaStyle;
    }
}

/**
 *  paceHolder还原endParaRPr
 * @param model
 * @param changedParagraphList
 */
export function restorePlaceHolderParaEndProps(model, changedParagraphList: IParagraph[]) {
    if (model.savedEndParaRPr && changedParagraphList.length > 0) {
        changedParagraphList[0].endParaRPr = model.savedEndParaRPr;
        model.savedEndParaRPr = undefined;
    }
    return changedParagraphList;
}
